/**
 * Finding something that can open a kind Setu does not render (NIP-89).
 *
 * Discovery is two hops and both are necessary: a kind-31989 recommendation says
 * "this app handles kind N" and names a handler address, and the handler's own
 * kind-31990 is what actually carries the URL template. Neither is usable alone.
 *
 * ## What this deliberately is not
 *
 * There is no registry to ask and this does not invent one. Recommendations are
 * ordinary events from ordinary accounts, so the answer is "some people say this
 * opens kind N" and never "the app for kind N". That is why the UI naming these
 * says *open in*, offers a list, and shows the destination host — the reader is
 * being handed third-party links, not a Setu-endorsed choice.
 *
 * ## Why it is bounded and lazy
 *
 * It runs only for an event we cannot render, only when one is actually on screen,
 * and every filter carries a `limit`. An unbounded 31989 query is a request for
 * every recommendation anyone has ever published; the handler hop is then bounded by
 * what the first hop returned. A reader who never meets an unknown kind pays nothing
 * at all, because the hook short-circuits on an undefined kind before touching the
 * engine.
 */

import {
  type AppHandler,
  handlerUrl,
  Kind,
  parseAppHandler,
  recommendedHandlers,
} from "@setu/protocol";
import { useEffect, useState } from "react";
import { useEngine } from "../../engine/EngineProvider";

/** Recommendations to consider. Enough for a menu, not a crawl. */
const MAX_RECOMMENDATIONS = 32;

/** Handlers to resolve from those recommendations. */
const MAX_HANDLERS = 16;

/** A handler that can open this entity, with the link that does it. */
export interface HandlerOption {
  readonly handler: AppHandler;
  /** Already validated https — see `handlerUrl`. */
  readonly url: string;
  /** The destination host, so the reader sees where they are being sent. */
  readonly host: string;
}

/**
 * Apps that claim to open `kind`, each with a URL for `bech32`.
 *
 * Empty until the two hops land, and empty forever when nobody has recommended
 * anything — which is the common case and renders as no menu rather than an empty
 * one.
 */
export function useAppHandlers(
  kind: number | undefined,
  bech32: string | undefined,
): readonly HandlerOption[] {
  const engine = useEngine();
  const [options, setOptions] = useState<readonly HandlerOption[]>([]);

  useEffect(() => {
    if (kind === undefined || !bech32) {
      setOptions([]);
      return;
    }
    let live = true;

    void (async () => {
      const relays = engine.relays;
      const fetchAll = async (filter: Record<string, unknown>) => {
        if (relays.length === 0) return;
        await engine.subscriptions
          .fetch({
            filters: relays.map((relay) => ({
              relay,
              filter: filter as never,
            })),
          })
          .catch(() => undefined);
      };

      // Hop one: who recommends anything for this kind. `#d` is the kind, which is
      // what makes this a narrow query rather than "every recommendation".
      const recommendationFilter = {
        kinds: [Kind.AppHandlerRecommendation],
        "#d": [String(kind)],
        limit: MAX_RECOMMENDATIONS,
      };
      await fetchAll(recommendationFilter);
      if (!live) return;

      const recommendations = await engine.store
        .query(recommendationFilter as never)
        .catch(() => []);
      if (!live) return;

      const addresses = new Set<string>();
      for (const row of recommendations) {
        for (const rec of recommendedHandlers(row.event)) {
          if (addresses.size >= MAX_HANDLERS) break;
          addresses.add(rec.address);
        }
      }
      if (addresses.size === 0) {
        setOptions([]);
        return;
      }

      // Hop two: the handlers themselves. Queried by author *and* identifier, both
      // taken from the addresses above, so this cannot widen into "every handler".
      const authors: string[] = [];
      const identifiers: string[] = [];
      for (const address of addresses) {
        const parts = address.split(":");
        const author = parts[1];
        if (author === undefined) continue;
        authors.push(author);
        identifiers.push(parts.slice(2).join(":"));
      }
      const handlerFilter = {
        kinds: [Kind.AppHandler],
        authors: [...new Set(authors)],
        "#d": [...new Set(identifiers)],
        limit: MAX_HANDLERS,
      };
      await fetchAll(handlerFilter);
      if (!live) return;

      const handlerRows = await engine.store
        .query(handlerFilter as never)
        .catch(() => []);
      if (!live) return;

      const seen = new Set<string>();
      const next: HandlerOption[] = [];
      for (const row of handlerRows) {
        const handler = parseAppHandler(row.event);
        if (handler === undefined || seen.has(handler.address)) continue;
        // The recommendation is a claim; the handler's own `k` tags are the
        // handler's claim. Requiring both keeps a recommendation from sending the
        // reader to an app that never said it opens this.
        if (!handler.kinds.includes(kind)) continue;
        const url = handlerUrl(handler, bech32);
        if (url === undefined) continue;
        seen.add(handler.address);
        next.push({ handler, url, host: new URL(url).hostname });
      }
      setOptions(next);
    })();

    return () => {
      live = false;
    };
  }, [engine, kind, bech32]);

  return options;
}
